import React, { useState } from "react";
import { subordinateComponents } from "../@utils/subordinateComponents";
import { PaginationContainer } from "./PaginationContainer";
import { PaginationItem } from "./PaginationItem";
import { PaginationMore } from "./PaginationMore";
import { PaginationNext } from "./PaginationNext";
import { PaginationPrev } from "./PaginationPrev";
import { usePagination } from "./usePagination";
import { usePaginationIndex } from "./usePaginationIndex";

export interface PaginationProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * 現在のページ番号、0はじまり。
   * @default 0
   */
  pageIndex?: number;
  /**
   * ページの数、1はじまり。
   * @default 5
   */
  pageSize?: number;
  /**
   * Paginationのボタンが押されたときに発火するメソッド
   */
  onChangePage?: (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    index: number,
  ) => void;
  children?: never;
  /**
   * 表示する長さ。7以下および偶数の値は指定できません。
   */
  visibleWidth?: number;

  /**
   * Paginationのサイズを指定出来ます。
   * @default "medium"
   */
  size?: "small" | "medium";
}

const Pagination_ = ({
  pageIndex: initialPageIndex = 0,
  pageSize: initialPageSize = 5,
  onChangePage,
  visibleWidth = 7,
  size,
  ...props
}: PaginationProps) => {
  const {
    items,
    nextItem,
    prevItem,
    setPageIndex,
    setPageSize,
    pageIndex,
    pageSize,
  } = usePagination({
    initialState: {
      pageIndex: initialPageIndex,
      pageSize: initialPageSize,
    },
    onChangePage,
  });
  const [prevPageIndex, setPrevPageIndex] = useState(initialPageIndex);
  if (initialPageIndex !== prevPageIndex) {
    setPrevPageIndex(initialPageIndex);
    setPageIndex(initialPageIndex);
  }
  const [prevPageSize, setPrevPageSize] = useState(initialPageSize);
  if (initialPageSize !== prevPageSize) {
    setPrevPageSize(initialPageSize);
    setPageSize(initialPageSize);
  }

  const pagination = usePaginationIndex(pageIndex, {
    total: pageSize,
    width: visibleWidth,
  });

  return (
    <PaginationContainer {...props} size={size}>
      <Pagination.Prev {...prevItem.getProps()} size={size} />
      {pagination.map((i) => {
        if (i === "next-more" || i === "prev-more") {
          return <PaginationMore key={i} size={size} />;
        } else {
          const item = items[i];
          const { key, current, onClick } = item.getProps();
          return (
            <PaginationItem
              key={key}
              current={current}
              onClick={onClick}
              size={size}
            >
              {item.render()}
            </PaginationItem>
          );
        }
      })}
      <Pagination.Next {...nextItem.getProps()} size={size} />
    </PaginationContainer>
  );
};

export const Pagination = subordinateComponents(Pagination_, {
  Container: PaginationContainer,
  Item: PaginationItem,
  Prev: PaginationPrev,
  Next: PaginationNext,
  More: PaginationMore,
});
